"use client";

import { useState } from "react";
import { toast } from "sonner";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  type SortDirection,
  sortEntries,
} from "@/lib/tool-kit/reference-checkers/alphabetize";

const BLANK_LINE_REGEX = /\n\s*\n/;
const NEWLINE_REGEX = /\n/;

/** English strings. A translated content file overrides these through `ui`. */
export const BIBLIOGRAPHY_ALPHABETIZER_STRINGS = {
  pasteBibliographyEntries: "Paste bibliography entries",
  sortOrder: "Sort order",
  aZ: "A → Z",
  zA: "Z → A",
  ignoreAAnTheAt: "Ignore “A”, “An”, “The” at start",
  sortEntries: "Sort Entries",
  sortedBibliography: "Sorted bibliography",
  copied: "Copied!",
  copyToClipboard: "Copy to clipboard",
  copyFailed: "Couldn't copy to clipboard. Please copy manually.",
  entriesPlaceholder:
    "Paste entries here, one per line or separated by blank lines...\n\nSmith, J. (2024). Title of article. Journal Name, 12(3), 45-67.\n\nJohnson, A. (2023). Another great article. Science Today, 8(1), 12-25.",
  sortingRulesByStyle: "Sorting Rules by Style",
  apa7thEdition: "APA 7th Edition",
  alphabetizeByTheFirstAuthor:
    "Alphabetize by the first author's last name. For works with the same first author, order by the second author's last name. For identical author groups, order by year (oldest first). Ignore “A,” “An,” and “The” at the beginning of titles.",
  mla9thEdition: "MLA 9th Edition",
  alphabetizeByTheFirstElement:
    "Alphabetize by the first element of each works cited entry (usually author last name). Ignore articles at the start of titles when no author is given. Number entries by last name, not first.",
  chicagoManualOfStyle: "Chicago Manual of Style",
  alphabetizeLetterByLetterIgnoring:
    "Chicago 18 prefers word-by-word alphabetizing, though letter-by-letter is also accepted. A comma interrupts the alphabetizing, so the surname is settled first. Ignore “The,” “A,” and “An” when they are the first word of a title. This tool sorts letter by letter, as APA and MLA do; the order differs only for multi-word surnames such as De Stefa and Descartes, so check those.",
} as const;

export default function BibliographyAlphabetizerWidget() {
  const strings = useToolUi(BIBLIOGRAPHY_ALPHABETIZER_STRINGS);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [direction, setDirection] = useState<SortDirection>("asc");
  const [ignoreArticles, setIgnoreArticles] = useState(true);
  const [copied, setCopied] = useState(false);

  function handleSort() {
    const raw = input.trim();
    if (!raw) {
      setOutput("");
      return;
    }

    // Split on blank lines first; if only one entry, split on single newlines
    let entries = raw.split(BLANK_LINE_REGEX).map((e) => e.trim());
    if (entries.length <= 1) {
      entries = raw.split(NEWLINE_REGEX).map((e) => e.trim());
    }
    entries = entries.filter((e) => e.length > 0);

    const sorted = sortEntries(entries, direction, ignoreArticles);
    setOutput(sorted.join("\n\n"));
  }

  async function handleCopy() {
    if (!output) {
      return;
    }
    if (await writeToClipboard(output)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error(strings.copyFailed);
    }
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-3">
        <label
          className="block font-medium text-foreground text-sm"
          htmlFor="bib-input"
        >
          {strings.pasteBibliographyEntries}
        </label>
        <textarea
          className="min-h-[200px] w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-foreground/30"
          id="bib-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.entriesPlaceholder}
          value={input}
        />
      </div>
      {/* Options */}
      <div className="flex flex-wrap items-center gap-6 rounded-lg border border-border bg-muted/30 p-4">
        <div className="space-y-1">
          <span className="block font-medium text-foreground text-sm">
            {strings.sortOrder}
          </span>
          <div className="flex gap-3">
            <label className="flex items-center gap-1.5 text-muted-foreground text-sm">
              <input
                checked={direction === "asc"}
                name="direction"
                onChange={() => setDirection("asc")}
                type="radio"
              />
              {strings.aZ}
            </label>
            <label className="flex items-center gap-1.5 text-muted-foreground text-sm">
              <input
                checked={direction === "desc"}
                name="direction"
                onChange={() => setDirection("desc")}
                type="radio"
              />
              {strings.zA}
            </label>
          </div>
        </div>

        <label className="flex items-center gap-2 text-muted-foreground text-sm">
          <input
            checked={ignoreArticles}
            onChange={(e) => setIgnoreArticles(e.target.checked)}
            type="checkbox"
          />
          {strings.ignoreAAnTheAt}
        </label>

        <button
          className="rounded-lg bg-foreground px-6 py-2 font-medium text-background text-sm transition-colors hover:bg-foreground/90"
          onClick={handleSort}
          type="button"
        >
          {strings.sortEntries}
        </button>
      </div>
      {/* Output */}
      {output && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="bib-output"
            >
              {strings.sortedBibliography}
            </label>
            <button
              className="rounded-md border border-border px-3 py-1 text-muted-foreground text-sm transition-colors hover:bg-muted hover:text-foreground"
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copyToClipboard}
            </button>
          </div>
          <textarea
            className="min-h-[200px] w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-foreground/30"
            id="bib-output"
            readOnly
            value={output}
          />
        </div>
      )}
      {/* Tips */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.sortingRulesByStyle}
        </h2>
        <div className="space-y-4 text-muted-foreground text-sm leading-relaxed">
          <div>
            <h3 className="font-medium text-foreground">
              {strings.apa7thEdition}
            </h3>
            <p>{strings.alphabetizeByTheFirstAuthor}</p>
          </div>
          <div>
            <h3 className="font-medium text-foreground">
              {strings.mla9thEdition}
            </h3>
            <p>{strings.alphabetizeByTheFirstElement}</p>
          </div>
          <div>
            <h3 className="font-medium text-foreground">
              {strings.chicagoManualOfStyle}
            </h3>
            <p>{strings.alphabetizeLetterByLetterIgnoring}</p>
          </div>
        </div>
      </section>
      {/* Related tools */}
    </>
  );
}
